import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Copy,
  ChevronDown,
  ImageUp,
  Loader2,
  MessagesSquare,
  MoreVertical,
  RotateCw,
  Search,
  ShieldCheck,
  Trash2,
  UserCog,
  X,
} from "lucide-react";
import {
  ACCENT_COLORS,
  AGENT_ROLES,
  AgentSetup,
  INTRO_MAX,
  toOrigin,
  PURPOSE_MAX,
  TIMEOUT_OPTIONS,
  resolvedIntro,
  updateSetup,
  useAgentSetup,
  WINDOW_COLORS,
} from "./setup-store";
import { inputCls, primaryBtn } from "./shared";
import { openSupportWidget } from "./support-widget";
import { addDomain as apiAddDomain, disableWidget, installWidget, loadSetup, removeDomain as apiRemoveDomain, saveIdentity, uploadImage } from "./setup-api";

// Support Bot > Setup (Figma "New Agent"): Agent Identity → Whitelisting →
// Test Agent → Review and Upload. Identity and domains save to the CRM
// backend (setup-api.ts); live testing and the install code will come from
// the install snippet.

const STEPS = ["Agent Identity", "Whitelisting", "Test Agent", "Review and Upload"];
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

const card = "bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800";
const label = "block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1.5";
const linkBtn = "inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700";

const Stepper: React.FC<{ step: number; onStep: (i: number) => void }> = ({ step, onStep }) => (
  <div className="flex items-center gap-3 overflow-x-auto pb-1">
    {STEPS.map((name, i) => (
      <React.Fragment key={name}>
        <button type="button" onClick={() => onStep(i)} className="flex items-center gap-2 shrink-0">
          <span
            className={`w-7 h-7 rounded-md flex items-center justify-center text-sm font-semibold border ${
              i < step
                ? "bg-emerald-500 border-emerald-500 text-white"
                : i === step
                ? "bg-emerald-50 border-emerald-500 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
                : "bg-indigo-50 border-indigo-50 text-gray-600 dark:bg-gray-800 dark:border-gray-800 dark:text-gray-400"
            }`}
          >
            {i < step ? <Check size={14} /> : i + 1}
          </span>
          <span className={`text-sm sm:text-base ${i === step ? "font-semibold text-gray-900 dark:text-gray-100" : "text-gray-600 dark:text-gray-400"}`}>{name}</span>
        </button>
        {i < STEPS.length - 1 && (
          <span className={`flex-1 min-w-[40px] h-0.5 rounded ${i < step ? "bg-gray-800 dark:bg-gray-300" : "bg-gray-200 dark:bg-gray-700"}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Uploads to /files/upload as soon as an image is picked; the local preview
// shows straight away and stays until the next load swaps in a signed URL.
const ImageUpload: React.FC<{ title: string; value: string; size: string; kind: "profile" | "logo"; placeholder: React.ReactNode }> = ({
  title,
  value,
  size,
  kind,
  placeholder,
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const pick = (file?: File) => {
    if (ref.current) ref.current.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) return toast.error("Use a JPEG or PNG image.");
    if (file.size > MAX_IMAGE_BYTES) return toast.error("Image must be 2 MB or smaller.");
    const reader = new FileReader();
    reader.onload = async () => {
      setUploading(true);
      const error = await uploadImage(kind, file, String(reader.result));
      setUploading(false);
      if (error) toast.error(error);
    };
    reader.readAsDataURL(file);
  };
  return (
    <div className="rounded-xl bg-emerald-50/50 dark:bg-gray-800/60 px-4 py-4 flex flex-col items-center text-center">
      <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">{title}</p>
      <div className="mt-3 w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden bg-white dark:bg-gray-900 flex items-center justify-center border border-gray-100 dark:border-gray-700">
        {uploading ? <Loader2 size={24} className="animate-spin text-indigo-500" /> : value ? <img src={value} alt="" className="w-full h-full object-cover" /> : placeholder}
      </div>
      <button type="button" disabled={uploading} onClick={() => ref.current?.click()} className={`${linkBtn} mt-3 underline underline-offset-2 disabled:opacity-50`}>
        {uploading ? "Uploading…" : value ? "Upload New" : "Upload"} <ImageUp size={16} />
      </button>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
        Supports JPEG or PNG <span className="text-emerald-500 mx-1">●</span> {size}
      </p>
      <input ref={ref} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
    </div>
  );
};

const Avatar: React.FC<{ src: string; name: string; className?: string }> = ({ src, name, className = "w-10 h-10" }) =>
  src ? (
    <img src={src} alt="" className={`${className} rounded-full object-cover shrink-0`} />
  ) : (
    <span className={`${className} rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300 flex items-center justify-center text-sm font-semibold shrink-0`}>
      {(name || "A").charAt(0).toUpperCase()}
    </span>
  );

// Under the preview: it's a picture, real chats happen in the live widget.
const PreviewNote: React.FC<{ s: AgentSetup }> = ({ s }) => {
  const allowed = s.domains.some((d) => d.domain === window.location.origin);
  const live = allowed && s.widgetEnabled && !!s.widgetKey;
  return (
    <div className="px-3 py-2.5 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-wrap items-center justify-between gap-2">
      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        Preview only.{" "}
        {live
          ? "To chat for real, use the chat bubble at the bottom right."
          : !allowed
            ? `To chat for real, add ${window.location.origin} on the Test Agent step.`
            : "To chat for real, turn on the widget on the Test Agent step."}
      </p>
      {live && (
        <button
          type="button"
          onClick={() => !openSupportWidget() && toast.error("The widget is still loading — try again in a moment.")}
          className="text-[11px] font-semibold text-indigo-700 dark:text-indigo-400 hover:underline"
        >
          Open live chat
        </button>
      )}
    </div>
  );
};

// The right-hand "Preview your Chat Widget" panel.
const WidgetPreview: React.FC<{ s: AgentSetup }> = ({ s }) => {
  const name = s.agentName || "Your agent";
  const intro = resolvedIntro(s) || `Hi, I am ${name}. How can I help you today?`;
  // Visitors always see the backend widget's pre-chat form (name, phone,
  // email) first; the toggle previews either screen. Mirrors widget.js's
  // form and its checks. Preview only: nothing typed here is sent anywhere.
  const [chatView, setChatView] = useState(false);
  const [visitor, setVisitor] = useState({ name: "", phone: "", email: "" });
  const [formError, setFormError] = useState("");
  const field = "w-full min-h-[40px] rounded-[7px] border border-[#d9d5e8] bg-white px-3 py-2 text-[13px] text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-offset-1";
  const startChat = (e: React.FormEvent) => {
    e.preventDefault();
    if (!visitor.name.trim() || !visitor.phone.trim() || !visitor.email.trim()) return setFormError("Fill in your name, phone and email.");
    if (!/^\+\d[\d\s()-]{6,}$/.test(visitor.phone.trim())) return setFormError("Enter a valid phone number with + and your country code.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(visitor.email.trim())) return setFormError("Check your name, phone and email, then try again.");
    setFormError("");
    setChatView(true);
  };
  return (
    <div className="space-y-2 h-full flex flex-col">
      <div className="flex w-fit rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5 text-xs font-medium">
        {[
          ["Pre-chat form", false],
          ["Chat", true],
        ].map(([label, value]) => (
          <button
            key={String(label)}
            type="button"
            onClick={() => setChatView(value as boolean)}
            className={`px-3 py-1.5 rounded-md transition-colors ${chatView === value ? "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm" : "text-gray-500 dark:text-gray-400"}`}
          >
            {label}
          </button>
        ))}
      </div>
    <div className="rounded-xl overflow-hidden border border-gray-100 dark:border-gray-800 flex flex-col flex-1 min-h-[460px]">
      <div className="flex items-start gap-3 px-4 py-3 border-b border-black/5" style={{ backgroundColor: s.windowColor }}>
        <Avatar src={s.profileImage} name={name} className="w-11 h-11" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-gray-900 truncate">{name}</p>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" /> Online
            </span>
          </div>
          {(s.brandName || s.brandLogo) && (
            <div className="flex items-center gap-1.5 mt-0.5">
              {s.brandLogo && <img src={s.brandLogo} alt="" className="w-4 h-4 rounded-full object-cover" />}
              <span className="text-sm text-gray-600 truncate">{s.brandName}</span>
            </div>
          )}
        </div>
        <X size={18} className="text-gray-700 shrink-0" />
      </div>
      {!chatView ? (
        <>
        <form onSubmit={startChat} noValidate className="flex-1 flex flex-col gap-3 p-[22px]" style={{ backgroundColor: s.windowColor }}>
          <h3 className="text-[19px] leading-snug font-semibold text-gray-900">Start a conversation</h3>
          <p className="text-xs text-[#625d74] -mt-1">Share your details so our support team can help you.</p>
          <label className="flex flex-col gap-1.5 text-xs font-medium text-gray-900">
            Name
            <input className={field} value={visitor.name} onChange={(e) => setVisitor({ ...visitor, name: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1.5 text-xs font-medium text-gray-900">
            Phone
            <input className={field} type="tel" placeholder="+234 801 234 5678" value={visitor.phone} onChange={(e) => setVisitor({ ...visitor, phone: e.target.value })} />
            <small className="text-[10px] font-normal text-[#625d74]">Include your country code, starting with +.</small>
          </label>
          <label className="flex flex-col gap-1.5 text-xs font-medium text-gray-900">
            Email
            <input className={field} type="email" value={visitor.email} onChange={(e) => setVisitor({ ...visitor, email: e.target.value })} />
          </label>
          <button type="submit" className="mt-0.5 rounded-[7px] py-2.5 text-[13px] font-semibold text-white" style={{ backgroundColor: s.accentColor }}>
            Start chat
          </button>
          {formError && <p className="text-[11px] text-[#685279]">{formError}</p>}
        </form>
        <PreviewNote s={s} />
        </>
      ) : (
      <>
      <div className="flex-1 px-4 py-4 space-y-4" style={{ backgroundColor: `color-mix(in srgb, ${s.windowColor} 55%, #E4E6F4)` }}>
        <p className="mx-auto w-fit text-xs font-medium text-gray-600 bg-gray-200/80 rounded px-6 py-1">Today</p>
        <div className="flex items-end gap-2">
          <Avatar src={s.profileImage} name={name} className="w-8 h-8" />
          <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 max-w-[85%] shadow-sm">
            <p className="text-sm text-gray-800 whitespace-pre-line">{intro}</p>
            <p className="text-right text-[11px] text-gray-400 mt-1.5">2:00pm</p>
          </div>
        </div>
      </div>
      <div className="px-3 py-3 bg-gray-200/70 dark:bg-gray-800">
        <div className="flex items-center gap-2 bg-white dark:bg-gray-900 rounded-xl pl-3 pr-1.5 py-1.5">
          <span className="flex-1 text-sm text-gray-400">Type a message. . .</span>
          <span className="px-3 py-1.5 rounded-md text-white text-sm font-medium" style={{ backgroundColor: s.accentColor }}>Send</span>
        </div>
      </div>
      <PreviewNote s={s} />
      </>
      )}
    </div>
    </div>
  );
};

// ── Steps ───────────────────────────────────────────────────────────────────

const IdentityStep: React.FC<{ s: AgentSetup }> = ({ s }) => (
  <div className="space-y-5">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <ImageUpload
        title="Profile Image"
        value={s.profileImage}
        size="512px X 512px"
        kind="profile"
        placeholder={<UserCog size={36} className="text-gray-300" />}
      />
      <ImageUpload
        title="Brand Logo"
        value={s.brandLogo}
        size="256px X 256px"
        kind="logo"
        placeholder={<ImageUp size={32} className="text-gray-300" />}
      />
    </div>

    <div>
      <p className={label}>Chat Window Color</p>
      <div className="flex flex-wrap gap-3">
        {WINDOW_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={`Colour ${c}`}
            onClick={() => updateSetup({ windowColor: c })}
            className={`w-10 h-10 rounded-full border-2 transition-all ${s.windowColor === c ? "border-gray-800 dark:border-gray-200 scale-105" : "border-transparent"}`}
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className={label}>Agent Name</label>
        <input className={`${inputCls} h-12`} value={s.agentName} placeholder="e.g. Rahul Kumar" onChange={(e) => updateSetup({ agentName: e.target.value })} />
      </div>
      <div>
        <label className={label}>Agent Role</label>
        <div className="relative">
          <select className={`${inputCls} h-12 appearance-none pr-10 bg-gray-50 dark:bg-gray-950`} value={s.agentRole} onChange={(e) => updateSetup({ agentRole: e.target.value })}>
            {(AGENT_ROLES.includes(s.agentRole) ? AGENT_ROLES : [s.agentRole, ...AGENT_ROLES]).map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
          <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        </div>
      </div>
    </div>

    <div>
      <label className={label}>Brand Name</label>
      <input className={`${inputCls} h-12`} value={s.brandName} maxLength={120} placeholder="Shown under the agent's name" onChange={(e) => updateSetup({ brandName: e.target.value })} />
    </div>

    <div>
      <p className={label}>Accent Color</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 -mt-1 mb-2">Used for the chat button, Send button and visitor messages.</p>
      <div className="flex flex-wrap gap-3">
        {ACCENT_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            aria-label={`Accent ${c}`}
            onClick={() => updateSetup({ accentColor: c })}
            className={`w-10 h-10 rounded-full border-2 transition-all ${s.accentColor.toLowerCase() === c.toLowerCase() ? "border-gray-800 dark:border-gray-200 scale-105 ring-2 ring-white ring-inset" : "border-transparent"}`}
            style={{ backgroundColor: c }}
          />
        ))}
      </div>
    </div>

    <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 space-y-4">
      <div>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Chat settings</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Visitors always fill in their name, phone and email before the chat starts.</p>
        {!s.chatSettingsSupported && <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">The inactivity setting will be saved once the chat service supports it.</p>}
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
        <div>
          <p className="text-sm text-gray-800 dark:text-gray-200">End chat after inactivity</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">If neither side sends a message for this long, the chat closes and the next message starts a new one.</p>
        </div>
        <div className="relative shrink-0 w-full sm:w-36">
          <select
            className={`${inputCls} h-11 appearance-none pr-9`}
            value={s.inactivityTimeoutMinutes}
            onChange={(e) => updateSetup({ inactivityTimeoutMinutes: Number(e.target.value) })}
          >
            {(TIMEOUT_OPTIONS.includes(s.inactivityTimeoutMinutes) ? TIMEOUT_OPTIONS : [...TIMEOUT_OPTIONS, s.inactivityTimeoutMinutes].sort((a, b) => a - b)).map((m) => (
              <option key={m} value={m}>
                {m < 60 ? `${m} minutes` : m === 60 ? "1 hour" : `${m / 60} hours`}
              </option>
            ))}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
        </div>
      </div>
    </div>

    <div>
      <label className={label}>Purpose</label>
      <textarea
        rows={3}
        maxLength={PURPOSE_MAX}
        className={`${inputCls} resize-none`}
        value={s.purpose}
        placeholder="What the agent helps customers with, and what it can do."
        onChange={(e) => updateSetup({ purpose: e.target.value })}
      />
      <p className="text-right text-xs text-gray-400 mt-1">
        {s.purpose.length}/ {PURPOSE_MAX}
      </p>
    </div>

    <div>
      <label className={label}>Short Intro Message</label>
      <textarea
        rows={2}
        maxLength={INTRO_MAX}
        className={`${inputCls} resize-none`}
        value={s.introMessage}
        placeholder={`Hi, I am ${s.agentName || "Rahul"} from “Company name”. I can help you with orders, deliveries, returns and product questions.`}
        onChange={(e) => updateSetup({ introMessage: e.target.value })}
      />
      <p className="text-right text-xs text-gray-400 mt-1">
        {s.introMessage.length}/ {INTRO_MAX}
      </p>
    </div>
  </div>
);

const formatAddedOn = (iso?: string) =>
  iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

const WhitelistingStep: React.FC<{ s: AgentSetup }> = ({ s }) => {
  const [domain, setDomain] = useState("");
  const [menuFor, setMenuFor] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);

  const addDomain = async () => {
    const origin = toOrigin(domain);
    if (!origin) return toast.error("Enter a website like https://www.example.com (no paths or wildcards).");
    if (s.domains.some((d) => d.domain === origin)) return toast.error(`${origin} is already on the list`);
    setBusy(true);
    const error = await apiAddDomain(origin);
    setBusy(false);
    if (error) return toast.error(error);
    setDomain("");
  };

  const removeDomain = async (id: string) => {
    setMenuFor(null);
    const error = await apiRemoveDomain(id);
    if (error) toast.error(error);
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            className={`${inputCls} h-11 pl-10`}
            value={domain}
            placeholder="Search domains to add. e.g. https://www.example.com"
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addDomain()}
          />
        </div>
        <button type="button" onClick={addDomain} disabled={busy} className={`${primaryBtn} shrink-0 disabled:opacity-60`}>
          {busy && <Loader2 size={16} className="animate-spin" />} Add Domain
        </button>
      </div>

      <div>
        <p className="text-base text-gray-800 dark:text-gray-200">Allowed Domains Listing</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-800 dark:text-gray-200 border-b border-gray-100 dark:border-gray-800">
                <th className="font-medium px-4 py-3">Domain Name</th>
                <th className="font-medium px-4 py-3">Added On</th>
                <th className="font-medium px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {s.domains.map(({ id, domain: d, created_at }) => (
                <tr key={id} className="text-gray-800 dark:text-gray-200">
                  <td className="px-4 py-4 break-all">{d}</td>
                  <td className="px-4 py-4 whitespace-nowrap">{formatAddedOn(created_at)}</td>
                  <td className="px-4 py-4 text-right">
                    <div className="relative inline-block">
                      <button
                        type="button"
                        aria-label={`Actions for ${d}`}
                        onClick={() => setMenuFor(menuFor === id ? null : id)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        <MoreVertical size={18} />
                      </button>
                      {menuFor === id && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => setMenuFor(null)} />
                          <div className="absolute right-0 mt-1 z-20 w-36 rounded-lg border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-lg py-1 text-left">
                            <button type="button" onClick={() => removeDomain(id)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10">
                              <Trash2 size={14} /> Remove
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {s.domains.length === 0 && (
            <p className="mt-3 text-sm text-gray-400 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl px-4 py-6 text-center">
              No domains added yet. Add each site and subdomain the agent should appear on.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// The real widget runs app-wide (support-widget.tsx) once it's on and this
// app's address is allowed — this step gets both done, then opens it.
const TestStep: React.FC<{ s: AgentSetup }> = ({ s }) => {
  const origin = window.location.origin;
  const allowed = s.domains.some((d) => d.domain === origin);
  const [busy, setBusy] = useState(false);
  const live = allowed && s.widgetEnabled && !!s.widgetKey;

  const run = async (fn: () => Promise<string | null>) => {
    setBusy(true);
    const error = await fn();
    setBusy(false);
    if (error) toast.error(error);
  };

  const steps = [
    {
      done: allowed,
      title: "Allow this app",
      detail: `Add ${origin} to the allowed websites so the test chat can run here.`,
      action: !allowed && (
        <button type="button" disabled={busy} onClick={() => run(() => apiAddDomain(origin))} className={`${linkBtn} disabled:opacity-50`}>
          Add {origin}
        </button>
      ),
    },
    {
      done: s.widgetEnabled && !!s.widgetKey,
      title: "Turn on the widget",
      detail: "Creates the widget's public key. This also enables it for your allowed websites.",
      action: !(s.widgetEnabled && s.widgetKey) && (
        <button type="button" disabled={busy} onClick={() => run(() => installWidget())} className={`${linkBtn} disabled:opacity-50`}>
          Turn on widget
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4 text-sm text-gray-700 dark:text-gray-300">
      <p>Chat with your agent exactly as a website visitor would. Your messages arrive in the CRM inbox under <b>Agents</b>, where you can reply.</p>
      <ol className="space-y-3">
        {steps.map((st, i) => (
          <li key={st.title} className="flex gap-3">
            <span className={`w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center shrink-0 ${st.done ? "bg-emerald-500 text-white" : "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300"}`}>
              {st.done ? <Check size={13} /> : i + 1}
            </span>
            <div>
              <p className="font-medium text-gray-800 dark:text-gray-200">{st.title}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{st.detail}</p>
              {st.action && <div className="mt-1.5">{st.action}</div>}
            </div>
          </li>
        ))}
      </ol>
      {live && (
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2.5 text-xs text-emerald-700 dark:text-emerald-300">
          The live widget is in the bottom-right corner of every page. Send a message, then reply from <b>Inbox → Agents</b>.
          <button
            type="button"
            onClick={() => !openSupportWidget() && toast.error("The widget is still loading — try again in a moment.")}
            className="block mt-1.5 font-semibold underline underline-offset-2"
          >
            Open the chat
          </button>
        </div>
      )}
    </div>
  );
};

const ReviewStep: React.FC<{ s: AgentSetup }> = ({ s }) => {
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<string | null>, done?: string) => {
    setBusy(true);
    const error = await fn();
    setBusy(false);
    if (error) toast.error(error);
    else if (done) toast.success(done);
  };
  const copy = () =>
    navigator.clipboard?.writeText(s.embedCode).then(
      () => toast.success("Install code copied"),
      () => toast.error("Couldn't copy — select and copy it manually.")
    );
  const rotate = () => {
    if (window.confirm("Replace the install key? The current code stops working and must be replaced on every website.")) run(() => installWidget(true), "New install code generated");
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Install on your website</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Paste this just before <code className="font-mono">&lt;/body&gt;</code> on every page — or in WordPress / Shopify's custom code (footer) setting.
        </p>
        {s.embedCode ? (
          <>
            <div className="relative mt-3">
              <pre className="text-[11px] leading-relaxed font-mono bg-gray-900 text-gray-100 rounded-xl p-4 pr-20 overflow-x-auto whitespace-pre-wrap break-all">{s.embedCode}</pre>
              <button
                type="button"
                onClick={copy}
                className="absolute top-2.5 right-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-white/10 hover:bg-white/20 rounded-md px-2.5 py-1.5"
              >
                <Copy size={13} /> Copy
              </button>
            </div>
            <div className="flex flex-wrap gap-4 mt-2">
              <button type="button" disabled={busy} onClick={rotate} className={`${linkBtn} text-xs disabled:opacity-50`}>
                <RotateCw size={13} /> Replace install key
              </button>
              {s.widgetEnabled && (
                <button type="button" disabled={busy} onClick={() => run(disableWidget, "Widget turned off")} className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:opacity-50">
                  Turn off widget
                </button>
              )}
            </div>
          </>
        ) : (
          <p className="mt-3 text-sm text-gray-400 border border-dashed border-gray-200 dark:border-gray-700 rounded-xl px-4 py-4 text-center">
            Click <b>Publish</b> to turn on the widget and generate your install code.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">Websites</p>
          <p className="font-semibold text-gray-800 dark:text-gray-200 mt-0.5 break-all">{s.domains.length ? s.domains.map((d) => d.domain).join(", ") : "None added"}</p>
        </div>
        <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
          <p className={`font-semibold mt-0.5 ${s.widgetEnabled ? "text-emerald-600" : "text-gray-800 dark:text-gray-200"}`}>{s.widgetEnabled ? "Live" : "Off"}</p>
        </div>
      </div>
    </div>
  );
};

// ── Page ────────────────────────────────────────────────────────────────────

const STEP_HEADINGS: { title: string; subtitle: string; card: string; icon: React.ElementType }[] = [
  { title: "Agent Identity", subtitle: "Give your agent a clear identity and define what it is responsible for", card: "Setup agent identity", icon: UserCog },
  { title: "Whitelisting Domains", subtitle: "Choose the websites your agent can appear on", card: "", icon: ShieldCheck },
  { title: "Test Agent", subtitle: "Try the live chat before it goes on your website", card: "How to test", icon: MessagesSquare },
  { title: "Review and Upload", subtitle: "Check your setup and publish", card: "Install and publish", icon: Check },
];

const SupportAgentSetup: React.FC = () => {
  const navigate = useNavigate();
  const s = useAgentSetup();
  const [step, setStep] = useState(0);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [saving, setSaving] = useState(false);
  const head = STEP_HEADINGS[step];
  const HeadIcon = head.icon;

  const canLeave = (from: number): string | null => {
    if (from === 0 && !s.agentName.trim()) return "Add the agent's name.";
    if (from === 0 && !s.introMessage.trim()) return "Add a short intro message.";
    return null;
  };

  useEffect(() => {
    loadSetup().then((error) => {
      if (error) toast.error(error);
      setLoadingSetup(false);
    });
  }, []);

  // Identity is saved (created the first time, PATCHed after) whenever the
  // user moves on from it; domains save as they're added or removed.
  const goTo = async (target: number) => {
    for (let i = step; i < target; i++) {
      const problem = canLeave(i);
      if (problem) {
        setStep(i);
        return toast.error(problem);
      }
    }
    if (step === 0 && target > 0) {
      setSaving(true);
      const error = await saveIdentity();
      setSaving(false);
      if (error) return toast.error(error);
    }
    setStep(target);
  };

  // A widget with no allowed websites can't be installed (no wildcard).
  const [publishing, setPublishing] = useState(false);
  const publish = async () => {
    if (!s.domains.length) {
      setStep(1);
      return toast.error("Add at least one website first.");
    }
    setPublishing(true);
    const error = (await saveIdentity()) || (await installWidget());
    setPublishing(false);
    if (error) return toast.error(error);
    toast.success("Support agent published", { description: "The install code stays available on Setup → Review and Upload." });
    // For now, publishing lands on the Knowledge dashboard. crm_admin can't
    // see Knowledge, so SupportAgentRoute sends them back to Setup.
    navigate("/support-bot/knowledge");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <div className={`${card} rounded-b-none px-4 sm:px-6 pt-5 pb-5 space-y-5`}>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => step > 0 && setStep(step - 1)}
            disabled={step === 0}
            aria-label="Back"
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl sm:text-2xl text-gray-400">
            Support Agent <span className="text-gray-300 mx-1">/</span> <span className="text-gray-900 dark:text-gray-100">Setup</span>
          </h1>
        </div>
        <Stepper step={step} onStep={goTo} />
      </div>

      <div className="bg-gray-100/70 dark:bg-gray-900/40 rounded-b-2xl px-4 sm:px-6 py-6">
        <h2 className="text-xl sm:text-2xl font-medium text-gray-900 dark:text-gray-100">{head.title}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{head.subtitle}</p>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-5">
          <div className={`${card} p-5 sm:p-6 flex flex-col`}>
            {head.card && (
              <p className="flex items-center gap-2 text-base text-gray-800 dark:text-gray-200 mb-5">
                <HeadIcon size={20} /> {head.card}
              </p>
            )}
            <div className="flex-1">
              {loadingSetup ? (
                <div className="h-full min-h-[320px] flex flex-col items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 size={24} className="animate-spin text-indigo-500 mb-2" />
                  Loading your agent setup…
                </div>
              ) : (
                <>
              {step === 0 && <IdentityStep s={s} />}
              {step === 1 && <WhitelistingStep s={s} />}
              {step === 2 && <TestStep s={s} />}
              {step === 3 && <ReviewStep s={s} />}
                </>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              {step > 0 && (
                <button type="button" onClick={() => setStep(step - 1)} className="px-4 py-2.5 text-sm font-medium text-gray-800 dark:text-gray-200 hover:text-gray-600">
                  Back
                </button>
              )}
              {step < STEPS.length - 1 ? (
                <button type="button" onClick={() => goTo(step + 1)} disabled={saving || loadingSetup} className={`${primaryBtn} disabled:opacity-60`}>
                  {saving && <Loader2 size={16} className="animate-spin" />} {saving ? "Saving…" : "Next"}
                </button>
              ) : (
                <button type="button" onClick={publish} disabled={publishing} className={`${primaryBtn} disabled:opacity-60`}>
                  {publishing && <Loader2 size={16} className="animate-spin" />} {s.widgetEnabled ? "Save & publish" : "Publish"}
                </button>
              )}
            </div>
          </div>

          <div className={`${card} p-5 sm:p-6 flex flex-col`}>
            <p className="flex items-center gap-2 text-base text-gray-800 dark:text-gray-200 mb-4">
              <MessagesSquare size={20} /> {step === 1 ? "Widget Preview" : "Preview your Chat Widget"}
            </p>
            <div className="flex-1">
              <WidgetPreview s={s} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportAgentSetup;
